import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import listPlugin from "@fullcalendar/list";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { AddAppointmentModal } from "../FullCalendar/AddAppointment";
import {
  fetchPractitionersByPractices,
  getAppointmentsByPracticeIds,
} from "../../api";
import { jwtDecode } from "jwt-decode";
import { EditAppointmentModal } from "./EditAppointments";
import {
  Col,
  Row,
  Container,
  Button,
  Offcanvas,
  Spinner,
} from "react-bootstrap";
import "./FullCalendar.css";

/* ---------------------- Shared Filters Content ---------------------- */
function FiltersContent({
  resourcesTitle,
  selectedResources,
  onToggleResource,
  canSelectMoreResources,
  selectedStatuses,
  onToggleStatus,
  statuses,
  onSelectAllResources,
  onClearResources,
  onSelectAllStatuses,
  onClearStatuses,
  userRole, // 👈 added
  userPermissions, // 👈 added
}) {
  const isPractitioner = userRole === "Practitioner";
  const isCalendarViewer = userPermissions === "CalendarViewer";
  const isRestricted = isPractitioner && isCalendarViewer;

  return (
    <>
      {/* Resources */}
      <div className="d-flex align-items-center justify-content-between">
        <h6 className="fw-bold text-primary mb-0">Resources</h6>
        {!isRestricted && (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={onClearResources}
            >
              Clear
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={onSelectAllResources}
            >
              Select all
            </Button>
          </div>
        )}
      </div>

      <div className="mt-2">
        {resourcesTitle?.length ? (
          resourcesTitle.map((resource) => {
            const checked = selectedResources.includes(resource.id);
            return (
              <div key={resource.id} className="form-check mb-1">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`resource-${resource.id}`}
                  checked={checked}
                  onChange={(e) =>
                    onToggleResource(resource.id, e.target.checked)
                  }
                  disabled={
                    isRestricted || (!checked && !canSelectMoreResources())
                  }
                />
                <label
                  htmlFor={`resource-${resource.id}`}
                  className={`form-check-label ${
                    isRestricted ? "text-muted" : "text-primary"
                  }`}
                >
                  {resource.title}
                </label>
              </div>
            );
          })
        ) : (
          <p className="text-muted">Loading resources…</p>
        )}
      </div>

      <hr className="my-3" />

      {/* Status */}
      <div className="d-flex align-items-center justify-content-between">
        <h6 className="fw-bold text-primary mb-0">Status</h6>
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={onClearStatuses}
          >
            Clear
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={onSelectAllStatuses}
          >
            Select all
          </Button>
        </div>
      </div>

      <div className="mt-2">
        {statuses.map((status) => (
          <div
            key={status.label}
            className="form-check d-flex align-items-center mb-1"
          >
            <input
              type="checkbox"
              className="form-check-input me-2"
              id={`status-${status.label}`}
              checked={selectedStatuses.includes(status.label)}
              onChange={(e) => onToggleStatus(status.label, e.target.checked)}
            />
            <label
              className="form-check-label d-flex align-items-center text-primary"
              htmlFor={`status-${status.label}`}
            >
              <i className={`fa-light ${status.icon} ${status.color} me-2`} />
              {status.label}
            </label>
          </div>
        ))}
      </div>
    </>
  );
}

/* ---------------------------- Main Calendar ---------------------------- */
export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [userId, setUserId] = useState();
  const [addModalShow, setAddModalShow] = useState(false);
  const [editModalShow, setEditModalShow] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [resourcesId, setResourcesId] = useState(null);
  const [resourcesName, setResourcesName] = useState(null);
  const [resourcesTitle, setResourcesTitle] = useState(null);
  const [practiceIds, setPracticeIds] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedResources, setSelectedResources] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [currentView, setCurrentView] = useState("resourceTimeGridDay");
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 933);
  const [userRole, setUserRole] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState("resourceTimeGridDay");
  const [userPermissions, setUserPermissions] = useState(null);
  const [loadedRange, setLoadedRange] = useState({ start: null, end: null });
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const calendarRef = useRef(null);

  const goToDayView = useCallback((dateLike) => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.changeView("resourceTimeGridDay", dateLike); // jump to day view on that date
  }, []);

  // Helper to calculate date window for fetching appointments
  const getDateWindow = useCallback((centerDate, monthsBuffer = 3) => {
    const center = centerDate ? new Date(centerDate) : new Date();
    const start = new Date(center);
    start.setMonth(start.getMonth() - monthsBuffer);
    const end = new Date(center);
    end.setMonth(end.getMonth() + monthsBuffer);
    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0],
    };
  }, []);

  const filteredEvents = useMemo(() => {
    return events.filter((e) =>
      selectedStatuses.includes(e.status ?? e.extendedProps?.status ?? "")
    );
  }, [events, selectedStatuses]);

  const filteredResources = useMemo(() => {
    if (!resourcesTitle) return [];

    const filtered = resourcesTitle.filter((r) =>
      selectedResources.includes(r.id)
    );

    if (currentView === "customFourDayView") {
      return filtered.slice(0, isMobile ? 1 : 3);
    }

    return filtered;
  }, [resourcesTitle, selectedResources, currentView, isMobile]);

  const renderEventContent = useCallback((arg) => {
    const { event, view } = arg;
    const ext = event.extendedProps || {};
    const isMonthView = view.type === "dayGridMonth";

    const isRecurring = !!ext.isRecurring;
    const hasIntakeForm = !!ext.intakeSessionTokenId;
    const isNotesDone = !!ext.isNotesDone;
    const isInvoiced = !!ext.isInvoiced;
    const isInvoiceEmailed = !!ext.isInvoiceEmailed;
    const isPaid = !!ext.isPaid;

    const icons = [];

    if (isRecurring)
      icons.push({
        icon: "fa-solid fa-rotate",
        title: "Recurring appointment",
      });

    if (hasIntakeForm)
      icons.push({
        icon: "fa-light fa-file-circle-info",
        title: "Intake form sent",
      });

    if (isNotesDone)
      icons.push({
        icon: "fa-light fa-clipboard-check",
        title: "Notes completed",
      });

    if (isInvoiced)
      icons.push({
        icon: "fa-light fa-file-invoice-dollar",
        title: "Invoiced",
      });

    if (isInvoiceEmailed)
      icons.push({
        icon: "fa-light fa-envelope",
        title: "Invoice emailed",
      });

    if (isPaid)
      icons.push({
        icon: "fa-light fa-credit-card",
        title: "Paid",
      });

    const title = event.title || "";

    // Month view: compact with colored dot + icons aligned to end
    if (isMonthView) {
      const bgColor = event.backgroundColor || "#3788d8";
      const iconsHTML = icons
        .map(
          (ic) =>
            `<i class="${ic.icon}" title="${ic.title}" style="font-size:0.65rem;color:#0f3490;"></i>`
        )
        .join("");

      return {
        html: `
        <div class="fc-daygrid-event-content" style="display:flex;align-items:center;gap:4px;overflow:hidden;width:100%;">
          <span class="fc-daygrid-event-dot" style="border-color:${bgColor};flex-shrink:0;"></span>
          <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:0.75rem;">${title}</span>
          <span style="display:flex;gap:1px;flex-shrink:0;margin-left:auto;">${iconsHTML}</span>
        </div>
      `,
      };
    }

    // Day/resource views: calculate duration for icon sizing
    const start = event.start;
    const end = event.end;
    let durationMinutes = 60; // default
    if (start && end) {
      durationMinutes = (end - start) / (1000 * 60);
    }
    const isShortAppt = durationMinutes <= 30;
    const iconSize = isShortAppt ? "0.65rem" : "0.9rem";

    const iconsHTML = icons
      .map(
        (ic) =>
          `<i class="${ic.icon} text-white" title="${ic.title}" style="font-size:${iconSize};"></i>`
      )
      .join("");

    return {
      html: `
      <div class="fc-event-title-container">
        <span class="event-title">${title}</span>
        <span class="event-icons">${iconsHTML}</span>
      </div>
    `,
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 933);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (!token) {
      console.error("No user token found.");
      setIsLoading(false);
      return;
    }

    const loadCalendarData = async () => {
      try {
        const decoded = jwtDecode(token);

        setUserId(decoded._id);
        setPracticeIds(decoded.practice || []);

        // Handle role as array or string
        const userRoles = Array.isArray(decoded.role)
          ? decoded.role
          : [decoded.role];
        const isPractitioner =
          userRoles.includes("Practitioner") && userRoles.length === 1;

        const roleToSet = isPractitioner ? "Practitioner" : "Admin";
        setUserRole(roleToSet);

        const userPermissions = Array.isArray(decoded.permissions)
          ? decoded.permissions
          : [decoded.permissions];
        const isCalViewer =
          userPermissions.includes("CalendarViewer") && userPermissions.length === 1;

        const permissionsToSet = isCalViewer ? "CalendarViewer" : null;
        setUserPermissions(permissionsToSet);

        const userName = `${decoded.firstName || ""} ${
          decoded.lastName || ""
        }`.trim();

        // Calculate initial date window (±3 months from today)
        const initialWindow = getDateWindow(new Date(), 3);

        const [appointmentsResponse, practitionersResponse] = await Promise.all(
          [
            getAppointmentsByPracticeIds(
              decoded.practice,
              initialWindow.start,
              initialWindow.end
            ),
            fetchPractitionersByPractices(decoded.practice),
          ]
        );

        // Store the loaded date range
        setLoadedRange(initialWindow);

        // if Practitioner role with CalendarViewer permission, limit to their own resource
        if (isPractitioner && isCalViewer) {
          const ownPractitioner = practitionersResponse.find(
            (p) =>
              p.title?.toLowerCase().includes(userName.toLowerCase()) ||
              p.id === decoded._id
          );

          if (ownPractitioner) {
            setResourcesTitle([ownPractitioner]);
            setSelectedResources([ownPractitioner.id]);
          } else {
            console.warn(
              "Practitioner not matched to resource list:",
              userName
            );
            setResourcesTitle([]);
            setSelectedResources([]);
          }
        } else {
          // normal access for admin roles or multiple roles
          setResourcesTitle(practitionersResponse);
          setSelectedResources(practitionersResponse.map((r) => r.id));
        }

        // set appointments
        setEvents(
          appointmentsResponse.map((evt) => ({
            ...evt,
            extendedProps: {
              ...evt,
              patientEmail: evt.patientEmail,
              patientPhone: evt.patientPhone,
              isNotesDone: !!evt.isNotesDone,
              isInvoiced: !!evt.isInvoiced,
              isInvoiceEmailed: !!evt.isInvoiceEmailed,
              isPaid: !!evt.isPaid,
              isRecurring: !!evt.isRecurring,
              recurringID: evt.recurringID || "",
            },
          }))
        );
      } catch (err) {
        console.error("Error loading calendar data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadCalendarData();
  }, []);

  const handleAddEvent = (newEvent) =>
    setEvents((prevEvents) => [...prevEvents, newEvent]);

  const dayClickAction = (info) => {
    if (activeView === "dayGridMonth") {
      // keep filters open (as requested)
      goToDayView(info.date);
      return;
    }

    // Resource views: open Add modal
    setSelectedDate(info.dateStr);
    setAddModalShow(true);

    if (info.resource) {
      setResourcesId(info.resource.id);
      setResourcesName(info.resource.title);
    }
  };

  const pad = (n) => String(n).padStart(2, "0");
  const toNaiveLocal = (v) => {
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v))
      return v;
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d)) return "";
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const compactAddress = (loc = {}) => {
    const cityProv = [loc.city, loc.province].filter(Boolean).join(", ");
    return [loc.address1, loc.address2, cityProv, loc.postalCode]
      .filter(Boolean)
      .join(" | ");
  };

  const handleEventClick = useCallback(
    (clickInfo) => {
      const ev = clickInfo.event;
      const ext = ev.extendedProps || {};

      // Prefer the DB strings we copied onto extendedProps
      const startStr = ext.startRaw || toNaiveLocal(ev.start);
      const endStr = ext.endRaw || toNaiveLocal(ev.end);

      // Build location from object; fall back to flattened fields if needed
      const loc = ext.location || {
        name: ext.locationName || "",
        address1: ext.locationAddress || "",
        address2: ext.locationAddress2 || "",
        city: ext.locationCity || "",
        province: ext.locationProvince || "",
        postalCode: ext.locationPostalCode || "",
      };

      const newSelectedEvent = {
        id: ev._def?.publicId || ev.id || "",
        title: ev.title || "",
        status: ext.status || "",
        patientFirstName: ext.patientFirstName || "",
        patientLastName: ext.patientLastName || "",
        patientEmail: ext.patientEmail || "",
        patientPhone: ext.patientPhone || "",
        notes: ext.notes || "",
        start: startStr,
        end: endStr,
        resourceId: ext.resourceId || ev._def?.resourceIds?.[0] || "",
        resourceName:
          ext.resourceName ??
          (ev._def?.resourceIds
            ? resourcesTitle.find((r) => r.id === ev._def.resourceIds[0])?.title
            : "") ??
          "",
        practiceId: ext.practiceId || "",
        serviceId: ext.serviceId || "",
        patientId: ext.patientId || "",

        // Location
        location: loc,
        locationName: loc.name || "",
        locationAddress: compactAddress(loc),
        isNotesDone: !!ext.isNotesDone,
        isInvoiced: !!ext.isInvoiced,
        isInvoiceEmailed: !!ext.isInvoiceEmailed,
        isPaid: !!ext.isPaid,
        isRecurring: !!ext.isRecurring,
        recurringID: ext.recurringID || "",
        intakeSessionTokenId: ext.intakeSessionTokenId || null,
        reminderLog: ext.reminderLog || [],
      };

      setSelectedEvent(newSelectedEvent);
    },
    [resourcesTitle]
  );

  useEffect(() => {
    if (selectedEvent && selectedEvent.resourceId !== "N/A") {
      setEditModalShow(true);
    }
  }, [selectedEvent]);

  // Fetch more appointments when navigating outside loaded range
  const fetchAppointmentsForRange = useCallback(
    async (start, end) => {
      if (isLoadingMore || !practiceIds.length) return;

      setIsLoadingMore(true);
      try {
        const newAppointments = await getAppointmentsByPracticeIds(
          practiceIds,
          start,
          end
        );

        if (newAppointments) {
          // Merge with existing events, avoiding duplicates by id
          setEvents((prev) => {
            const existingIds = new Set(prev.map((e) => e.id));
            const newEvents = newAppointments
              .filter((e) => !existingIds.has(e.id))
              .map((evt) => ({
                ...evt,
                extendedProps: {
                  ...evt,
                  patientEmail: evt.patientEmail,
                  patientPhone: evt.patientPhone,
                  isNotesDone: !!evt.isNotesDone,
                  isInvoiced: !!evt.isInvoiced,
                  isInvoiceEmailed: !!evt.isInvoiceEmailed,
                  isPaid: !!evt.isPaid,
                  isRecurring: !!evt.isRecurring,
                  recurringID: evt.recurringID || "",
                },
              }));
            return [...prev, ...newEvents];
          });

          // Expand the loaded range
          setLoadedRange((prev) => ({
            start: start < prev.start ? start : prev.start,
            end: end > prev.end ? end : prev.end,
          }));
        }
      } catch (err) {
        console.error("Error fetching more appointments:", err);
      } finally {
        setIsLoadingMore(false);
      }
    },
    [isLoadingMore, practiceIds]
  );

  const handleViewChange = useCallback(
    (info) => {
      const newView = info.view.type;
      setCurrentView(newView);

      if (newView === "customFourDayView") {
        if (isMobile && selectedResources.length > 1) {
          setSelectedResources(selectedResources.slice(0, 1));
        } else if (!isMobile && selectedResources.length > 3) {
          setSelectedResources(selectedResources.slice(0, 3));
        }
      }

      // Check if we need to fetch more appointments based on visible date range
      const viewStart = info.view.activeStart?.toISOString().split("T")[0];
      const viewEnd = info.view.activeEnd?.toISOString().split("T")[0];

      if (viewStart && viewEnd && loadedRange.start && loadedRange.end) {
        const needsEarlierData = viewStart < loadedRange.start;
        const needsLaterData = viewEnd > loadedRange.end;

        if (needsEarlierData || needsLaterData) {
          // Calculate new window centered on the view with 3-month buffer
          const centerDate = new Date(info.view.currentStart);
          const newWindow = getDateWindow(centerDate, 3);
          fetchAppointmentsForRange(newWindow.start, newWindow.end);
        }
      }
    },
    [
      isMobile,
      selectedResources,
      loadedRange,
      getDateWindow,
      fetchAppointmentsForRange,
    ]
  );

  // Resource toggle with 4-day limits preserved
  const handleResourceSelection = (resourceId, isChecked) => {
    setSelectedResources((prevSelected) => {
      if (isChecked) {
        if (activeView === "dayGridMonth") {
          // month view = single select (replace)
          return [resourceId];
        }

        if (currentView === "customFourDayView") {
          if (isMobile && prevSelected.length >= 1) return prevSelected;
          if (!isMobile && prevSelected.length >= 3) return prevSelected;
        }

        return [...prevSelected, resourceId];
      } else {
        return prevSelected.filter((id) => id !== resourceId);
      }
    });
  };

  // Helper for Offcanvas to know if we can add more resources
  const canSelectMoreResources = () => {
    if (activeView === "dayGridMonth") return selectedResources.length < 1;

    if (currentView !== "customFourDayView") return true;
    const limit = isMobile ? 1 : 3;
    return selectedResources.length < limit;
  };

  useEffect(() => {
    if (!resourcesTitle?.length) return;

    // Don't auto-select all in month view (month is single-resource)
    if (activeView === "dayGridMonth") return;

    setSelectedResources(resourcesTitle.map((r) => r.id));
  }, [resourcesTitle, activeView]);

  const statuses = useMemo(
    () => [
      { label: "Pending", icon: "fa-circle-question", color: "text-pending" },
      {
        label: "New Patient",
        icon: "fa-triangle-exclamation",
        color: "text-danger",
      },
      { label: "Confirmed", icon: "fa-calendar-check", color: "text-success" },
      { label: "No Show", icon: "fa-ban", color: "text-danger" },
      { label: "Arrived", icon: "fa-location-dot", color: "text-secondary" },
      { label: "In Session", icon: "fa-spinner", color: "text-info" },
      { label: "Completed", icon: "fa-circle-check", color: "text-primary" },
      { label: "Scheduling", icon: "fa-clock", color: "text-muted" },
      { label: "Cancelled", icon: "fa-circle-xmark", color: "text-cancelled" },
    ],
    []
  );

  useEffect(() => {
    const defaultStatuses = statuses
      .filter((s) => s.label !== "Cancelled")
      .map((s) => s.label);
    setSelectedStatuses(defaultStatuses);
  }, [statuses]);

  const handleStatusSelection = (status, isChecked) => {
    setSelectedStatuses((prev) =>
      isChecked ? [...prev, status] : prev.filter((s) => s !== status)
    );
  };

  // Bulk actions
  const selectAllResources = () => {
    if (!resourcesTitle?.length) return;

    if (activeView === "dayGridMonth") {
      setSelectedResources([resourcesTitle[0].id]); // month = first only
      return;
    }

    const limit =
      currentView === "customFourDayView" ? (isMobile ? 1 : 3) : Infinity;

    setSelectedResources(resourcesTitle.slice(0, limit).map((r) => r.id));
  };

  const clearResources = () => setSelectedResources([]);

  const selectAllStatuses = () =>
    setSelectedStatuses(statuses.map((s) => s.label));
  const clearStatuses = () => setSelectedStatuses([]);

  // Memoize filtered resources for performance

  useEffect(() => {
    if (activeView !== "dayGridMonth") return;

    // pick the first selected resource (or fall back to first available)
    setSelectedResources((prev) => {
      const first = prev?.[0] || resourcesTitle?.[0]?.id || null;

      return first ? [first] : [];
    });
  }, [activeView, resourcesTitle]);

  const selectedResourceId = selectedResources?.[0] || null;

  // Get the selected therapist name for month view header
  const selectedTherapistName = useMemo(() => {
    if (!selectedResourceId || !resourcesTitle?.length) return "";
    const resource = resourcesTitle.find((r) => r.id === selectedResourceId);
    return resource?.title || "";
  }, [selectedResourceId, resourcesTitle]);

  // Add/remove therapist name below the title in month view
  useEffect(() => {
    const calendarEl = calendarRef.current?.elRef?.current;
    if (!calendarEl) return;

    const titleEl = calendarEl.querySelector(".fc-toolbar-title");
    if (!titleEl) return;

    // Remove any existing therapist subtitle
    const existingSubtitle = titleEl.querySelector(".therapist-subtitle");
    if (existingSubtitle) existingSubtitle.remove();

    // Only add in month view
    if (activeView === "dayGridMonth" && selectedTherapistName) {
      const subtitle = document.createElement("div");
      subtitle.className = "therapist-subtitle";
      subtitle.style.fontSize = "0.85em";
      subtitle.style.fontWeight = "normal";
      subtitle.style.color = "#21D1D4";
      subtitle.style.marginTop = "4px";
      subtitle.style.textAlign = "center";
      subtitle.textContent = selectedTherapistName;
      titleEl.appendChild(subtitle);
    }
  }, [activeView, selectedTherapistName]);

  const monthEvents = useMemo(() => {
    if (!selectedResourceId) return [];

    // keep your status filtering AND add resource filtering
    return filteredEvents.filter((e) => {
      const rid = e.resourceId ?? e.extendedProps?.resourceId;
      return rid === selectedResourceId;
    });
  }, [filteredEvents, selectedResourceId]);

  return (
    <div className="pt-5 mt-5" style={{ minHeight: "100vh" }}>
      {isLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "50vh" }}
        >
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading calendar...</span>
          </Spinner>
        </div>
      ) : (
        <div className="ms-1">
          <Row>
            {/* No sidebar anymore – filter lives in Offcanvas */}

            {/* Calendar */}
            <Col xs={12}>
              <FullCalendar
                className="calendar-container"
                ref={calendarRef}
                height={isMobile ? "75vh" : "85vh"}
                schedulerLicenseKey="0328351339-fcs-1748930365"
                plugins={[
                  dayGridPlugin,
                  timeGridPlugin,
                  resourceTimeGridPlugin,
                  interactionPlugin,
                  listPlugin,
                  resourceTimelinePlugin,
                ]}
                dateClick={dayClickAction}
                nowIndicator={true}
                views={{
                  customFourDayView: {
                    type: "resourceTimeGrid",
                    duration: { days: 4 },
                    buttonText: "4 DAYS",
                  },
                  day: {
                    titleFormat: {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      weekday: "short",
                    },
                  },
                }}
                buttonText={{
                  today: "TODAY",
                  month: "MONTH",
                  week: "WEEK",
                  day: "DAY",
                  resourceTimeGridDay: isMobile ? "DAY" : "DAY VIEW",
                  customFourDayView: isMobile ? "4D" : "4 DAYS",
                  timeGridMonth: isMobile ? "MONTH" : "MONTH VIEW",
                }}
                slotMinTime={"06:00:00"}
                slotMaxTime={"19:00:00"}
                initialView="resourceTimeGridDay"
                datesSet={(info) => {
                  setActiveView(info.view.type);
                  handleViewChange(info);
                }}
                eventClick={handleEventClick}
                titleFormat={
                  isMobile
                    ? { year: "2-digit", month: "short", day: "numeric" }
                    : { year: "numeric", month: "long", day: "numeric" }
                }
                headerToolbar={
                  isMobile
                    ? { left: "filterToggle", center: "title", right: "today" }
                    : {
                        left: "filterToggle,prev,next today",
                        center: "title",
                        right:
                          "dayGridMonth,resourceTimeGridDay,customFourDayView",
                      }
                }
                footerToolbar={
                  isMobile
                    ? {
                        left: "prev,next",
                        center: "",
                        right:
                          "dayGridMonth,resourceTimeGridDay,customFourDayView",
                      }
                    : false
                }
                customButtons={{
                  filterToggle: {
                    text: showFilters ? "HIDE" : "FILTER",
                    className: "btn btn-sm btn-outline-primary me-2 rounded-0",
                    style: { fontSize: "0.7rem", fontWeight: "600" },
                    click: () => setShowFilters((v) => !v),
                  },
                }}
                datesAboveResources={true}
                resources={filteredResources}
                events={
                  activeView === "dayGridMonth" ? monthEvents : filteredEvents
                }
                dayMaxEvents={activeView === "dayGridMonth" ? 3 : undefined}
                displayEventTime={activeView === "dayGridMonth" ? false : true}
                eventContent={renderEventContent}
                editable={false}
              />

              {/* Edit Modal */}
              <EditAppointmentModal
                event={selectedEvent}
                show={editModalShow}
                resources={selectedResources}
                practiceIds={practiceIds}
                setEvents={setEvents}
                onHide={() => {
                  setEditModalShow(false);
                  setSelectedEvent(null);
                }}
              />

              {/* Add Modal */}
              <AddAppointmentModal
                show={addModalShow}
                onHide={() => setAddModalShow(false)}
                selectedDate={selectedDate}
                resourcesId={resourcesId}
                resourcesName={resourcesName}
                onSave={handleAddEvent}
                events={events}
                practiceIds={practiceIds}
                setEvents={setEvents}
              />
            </Col>
          </Row>
        </div>
      )}

      {/* Slide-out Filters (right side, like receipts) */}
      {!isLoading && (
        <Offcanvas
          placement="start"
          show={showFilters}
          onHide={() => setShowFilters(false)}
          scroll
          backdrop
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title className="text-primary">Filter</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Container className="p-0">
              <FiltersContent
                resourcesTitle={resourcesTitle}
                selectedResources={selectedResources}
                onToggleResource={handleResourceSelection}
                canSelectMoreResources={canSelectMoreResources}
                selectedStatuses={selectedStatuses}
                onToggleStatus={handleStatusSelection}
                statuses={statuses}
                onSelectAllResources={selectAllResources}
                onClearResources={clearResources}
                onSelectAllStatuses={selectAllStatuses}
                onClearStatuses={clearStatuses}
                userRole={userRole} // 👈 pass role down
                userPermissions={userPermissions} // 👈 pass permissions down
              />

              <div className="d-flex justify-content-end mt-3 gap-2">
                <Button
                  variant="outline-secondary"
                  className="rounded-0"
                  onClick={() => setShowFilters(false)}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  className="rounded-0"
                  onClick={() => setShowFilters(false)}
                >
                  Apply
                </Button>
              </div>
            </Container>
          </Offcanvas.Body>
        </Offcanvas>
      )}
    </div>
  );
}
